// A fancyful colors demo page; props and credits to,
// https://roobinium.io/, for a beatiful and easy to use design.

import { DemoHeader, DemoInfoBox } from '@/components/demo';

export const ColorsDemo = () => {
    return (
        <div className="min-h-screen p-12">
            <div className="max-w-6xl mx-auto space-y-16">
                <DemoHeader badge="#styles" title="colors" />

                <DemoInfoBox 
                    description={
                        <>
                            Colors are accessed as <span className="text-primary font-bold">Tailwind/DaisyUI classes</span>, not React components. 
                            Use them directly in your <code className="px-2 py-1 bg-base-300 rounded text-sm">className</code> props.
                        </>
                    }
                    example="bg-primary text-primary-content"
                />

                {/* General Colors Section */}
                <div className="space-y-5">
                    <div className="relative pb-5 border-b border-gray-400/60">
                        <h3 className="font-montserrat text-base font-bold">01_general</h3>
                        <p className="absolute right-0 top-7 text-sm font-montserrat font-medium">2 styles</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {/* White */}
                        <div className="w-48 h-72 bg-white rounded-[20px] overflow-hidden">
                            <div className="absolute bottom-4 left-4 space-y-3">
                                <div className="space-y-0">
                                    <div className="text-slate-900 text-sm font-semibold font-montserrat leading-5">01_white</div>
                                    <div className="text-slate-900/80 text-sm font-medium font-montserrat leading-5">#FFFFFF</div>
                                </div>
                                <div className="text-slate-900/80 text-sm font-medium font-montserrat leading-5">
                                    <code className="bg-slate-900/10 px-2 py-1 rounded text-xs">text-white</code>
                                </div>
                            </div>
                        </div>

                        {/* Dark */}
                        <div className="w-48 h-72 bg-gray-900 rounded-[20px] overflow-hidden relative">
                            <div className="absolute bottom-4 left-4 space-y-3">
                                <div className="space-y-0">
                                    <div className="text-white text-sm font-semibold font-montserrat leading-5">02_dark</div>
                                    <div className="text-white/80 text-sm font-medium font-montserrat leading-5">#111827</div>
                                </div>
                                <div className="text-white/80 text-sm font-medium font-montserrat leading-5">
                                    <code className="bg-white/10 px-2 py-1 rounded text-xs">bg-base-100</code>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Accent Colors Section */}
                <div className="space-y-5">
                    <div className="relative pb-5 border-b border-gray-400/60">
                        <h3 className="font-montserrat text-base font-bold">02_accent</h3>
                        <p className="absolute right-0 top-7 text-sm font-montserrat font-medium">3 styles</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {/* Primary (Lime) */}
                        <div className="w-48 h-72 bg-primary rounded-[20px] overflow-hidden relative">
                            <div className="absolute bottom-4 left-4 space-y-3">
                                <div className="space-y-0">
                                    <div className="text-primary-content text-sm font-semibold font-montserrat leading-5">03_primary</div>
                                    <div className="text-primary-content/80 text-sm font-medium font-montserrat leading-5">#d4ff00</div>
                                </div>
                                <div className="text-primary-content/80 text-sm font-medium font-montserrat leading-5">
                                    <code className="bg-primary-content/10 px-2 py-1 rounded text-xs">bg-primary</code>
                                </div>
                            </div>
                        </div>

                        {/* Secondary (Cyan) */}
                        <div className="w-48 h-72 bg-secondary rounded-[20px] overflow-hidden relative">
                            <div className="absolute bottom-4 left-4 space-y-3">
                                <div className="space-y-0">
                                    <div className="text-secondary-content text-sm font-semibold font-montserrat leading-5">04_secondary</div>
                                    <div className="text-secondary-content/80 text-sm font-medium font-montserrat leading-5">#22d3ee</div>
                                </div>
                                <div className="text-secondary-content/80 text-sm font-medium font-montserrat leading-5">
                                    <code className="bg-secondary-content/10 px-2 py-1 rounded text-xs">bg-secondary</code>
                                </div>
                            </div>
                        </div>

                        {/* Accent (Sky) */}
                        <div className="w-48 h-72 bg-accent rounded-[20px] overflow-hidden relative">
                            <div className="absolute bottom-4 left-4 space-y-3">
                                <div className="space-y-0">
                                    <div className="text-accent-content text-sm font-semibold font-montserrat leading-5">05_accent</div>
                                    <div className="text-accent-content/80 text-sm font-medium font-montserrat leading-5">#a5f3fc</div>
                                </div>
                                <div className="text-accent-content/80 text-sm font-medium font-montserrat leading-5">
                                    <code className="bg-accent-content/10 px-2 py-1 rounded text-xs">bg-accent</code>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Background Colors Section */}
                <div className="space-y-5">
                    <div className="relative pb-5 border-b border-gray-400/60">
                        <h3 className="font-montserrat text-base font-bold">03_backgrounds</h3>
                        <p className="absolute right-0 top-7 text-sm font-montserrat font-medium">4 styles</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {/* Base 100 */}
                        <div className="w-48 h-72 bg-base-100 rounded-[20px] overflow-hidden relative border border-base-300">
                            <div className="absolute bottom-4 left-4 space-y-3">
                                <div className="space-y-0">
                                    <div className="text-base-content text-sm font-semibold font-montserrat leading-5">06_base-100</div>
                                    <div className="text-base-content/80 text-sm font-medium font-montserrat leading-5">#111827</div>
                                </div>
                                <div className="text-base-content/80 text-sm font-medium font-montserrat leading-5">
                                    <code className="bg-base-content/10 px-2 py-1 rounded text-xs">bg-base-100</code>
                                </div>
                            </div>
                        </div>

                        {/* Base 200 */}
                        <div className="w-48 h-72 bg-base-200 rounded-[20px] overflow-hidden relative">
                            <div className="absolute bottom-4 left-4 space-y-3">
                                <div className="space-y-0">
                                    <div className="text-base-content text-sm font-semibold font-montserrat leading-5">07_base-200</div>
                                    <div className="text-base-content/80 text-sm font-medium font-montserrat leading-5">#1f2937</div>
                                </div>
                                <div className="text-base-content/80 text-sm font-medium font-montserrat leading-5">
                                    <code className="bg-base-content/10 px-2 py-1 rounded text-xs">bg-base-200</code>
                                </div>
                            </div>
                        </div>

                        {/* Base 300 */}
                        <div className="w-48 h-72 bg-base-300 rounded-[20px] overflow-hidden relative">
                            <div className="absolute bottom-4 left-4 space-y-3">
                                <div className="space-y-0">
                                    <div className="text-base-content text-sm font-semibold font-montserrat leading-5">08_base-300</div>
                                    <div className="text-base-content/80 text-sm font-medium font-montserrat leading-5">#374151</div>
                                </div>
                                <div className="text-base-content/80 text-sm font-medium font-montserrat leading-5">
                                    <code className="bg-base-content/10 px-2 py-1 rounded text-xs">bg-base-300</code>
                                </div>
                            </div>
                        </div>

                        {/* Neutral */}
                        <div className="w-48 h-72 bg-neutral rounded-[20px] overflow-hidden relative">
                            <div className="absolute bottom-4 left-4 space-y-3">
                                <div className="space-y-0">
                                    <div className="text-neutral-content text-sm font-semibold font-montserrat leading-5">09_neutral</div>
                                    <div className="text-neutral-content/80 text-sm font-medium font-montserrat leading-5">#18181b</div>
                                </div>
                                <div className="text-neutral-content/80 text-sm font-medium font-montserrat leading-5">
                                    <code className="bg-neutral-content/10 px-2 py-1 rounded text-xs">bg-neutral</code>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Semantic Colors Section */}
                <div className="space-y-5">
                    <div className="relative pb-5 border-b border-gray-400/60">
                        <h3 className="font-montserrat text-base font-bold">04_semantic</h3>
                        <p className="absolute right-0 top-7 text-sm font-montserrat font-medium">4 styles</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {/* Info */}
                        <div className="w-48 h-72 bg-info rounded-[20px] overflow-hidden relative">
                            <div className="absolute bottom-4 left-4 space-y-3">
                                <div className="space-y-0">
                                    <div className="text-info-content text-sm font-semibold font-montserrat leading-5">10_info</div>
                                    <div className="text-info-content/80 text-sm font-medium font-montserrat leading-5">#22d3ee</div>
                                </div>
                                <div className="text-info-content/80 text-sm font-medium font-montserrat leading-5">
                                    <code className="bg-info-content/10 px-2 py-1 rounded text-xs">bg-info</code>
                                </div>
                            </div>
                        </div>

                        {/* Success */}
                        <div className="w-48 h-72 bg-success rounded-[20px] overflow-hidden relative">
                            <div className="absolute bottom-4 left-4 space-y-3">
                                <div className="space-y-0">
                                    <div className="text-success-content text-sm font-semibold font-montserrat leading-5">11_success</div>
                                    <div className="text-success-content/80 text-sm font-medium font-montserrat leading-5">#4ade80</div>
                                </div>
                                <div className="text-success-content/80 text-sm font-medium font-montserrat leading-5">
                                    <code className="bg-success-content/10 px-2 py-1 rounded text-xs">bg-success</code>
                                </div>
                            </div>
                        </div>

                        {/* Warning */}
                        <div className="w-48 h-72 bg-warning rounded-[20px] overflow-hidden relative">
                            <div className="absolute bottom-4 left-4 space-y-3">
                                <div className="space-y-0">
                                    <div className="text-warning-content text-sm font-semibold font-montserrat leading-5">12_warning</div>
                                    <div className="text-warning-content/80 text-sm font-medium font-montserrat leading-5">#fbbf24</div>
                                </div>
                                <div className="text-warning-content/80 text-sm font-medium font-montserrat leading-5">
                                    <code className="bg-warning-content/10 px-2 py-1 rounded text-xs">bg-warning</code>
                                </div>
                            </div>
                        </div>

                        {/* Error */}
                        <div className="w-48 h-72 bg-error rounded-[20px] overflow-hidden relative">
                            <div className="absolute bottom-4 left-4 space-y-3">
                                <div className="space-y-0">
                                    <div className="text-error-content text-sm font-semibold font-montserrat leading-5">13_error</div>
                                    <div className="text-error-content/80 text-sm font-medium font-montserrat leading-5">#f87171</div>
                                </div>
                                <div className="text-error-content/80 text-sm font-medium font-montserrat leading-5">
                                    <code className="bg-error-content/10 px-2 py-1 rounded text-xs">bg-error</code>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};